import React, { useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import type { ExternalIdentifier } from 'types/writer'

import { createPublisher } from '@disco/state-management/actions/rights-data-actions'
import { updateSplit } from '@disco/state-management/reducers/rightsManagement'
import { getPublishers } from '@disco/state-management/selectors/contacts'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { BaseDialog } from '../../components/base-dialog'
import { useCreateRightsEntity } from '../../hooks'
import type { RightsDataModalLocationState } from '../../types'
import { findDuplicateName } from '../../utils/validation'

/**
 * CreatePublisherModal - Modal for creating a new publisher
 *
 * This modal allows users to create a new publisher by providing a name
 * and optional IPI and ISNI identifiers.
 *
 * When a publisher is created from a split row (via splitId and splitsKey in location state),
 * the split is automatically updated with the new publisher.
 */
export function CreatePublisherModal() {
  const dispatch = useDispatch()
  const location = useLocation()
  const publishers = useSelector(getPublishers)
  const {
    searchText = '',
    splitId,
    splitsKey,
  } = (location.state as RightsDataModalLocationState) || {}

  const createEntity = useCreateRightsEntity({
    entityType: 'Publisher',
    createThunk: createPublisher,
  })

  const [inputValue, setInputValue] = useState(searchText)

  const existingPublisherNames = useMemo(
    () => publishers.map(p => p.name),
    [publishers],
  )

  const duplicatePublisherName = useMemo(
    () => findDuplicateName(inputValue, existingPublisherNames),
    [inputValue, existingPublisherNames],
  )

  const warningMessage = duplicatePublisherName
    ? `${duplicatePublisherName} already exists. Please double-check if this is a duplicate before saving.`
    : null

  const handleNameChange = useCallback((value: string) => {
    setInputValue(value)
  }, [])

  const handleSave = useCallback(
    async (
      name: string,
      externalIdentifiers: ExternalIdentifier[] | undefined,
    ) => {
      const createdPublisherId = await createEntity(name, externalIdentifiers)

      // Auto-select the created publisher in the split that triggered the creation
      if (splitId && splitsKey) {
        dispatch(
          updateSplit({
            trackId: splitsKey,
            splitId,
            changes: { publisherId: createdPublisherId },
          }),
        )
      }
    },
    [dispatch, splitId, splitsKey, createEntity],
  )

  return (
    <BaseDialog
      heading="Create new publisher"
      nameFieldLabel="Publisher name"
      onSave={handleSave}
      initialName={searchText}
      warningMessage={warningMessage}
      onNameChange={handleNameChange}
      showIPI
      showISNI
      open
    />
  )
}

export default CreatePublisherModal
